import { useEffect, useMemo, useState } from 'react';
import { Users, UserCheck, School, Activity } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { adminSchoolApi, adminUsersApi } from '@/api/admin';
import { getUserData } from '@/utils/auth';
import { trackEvent, attachUserProps } from '@/analytics/analytics';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export default function AdminDashboard() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [metrics, setMetrics] = useState({
    totalUsers: 0,
    activeStudents: 0,
    activeTeachers: 0,
  });
  const [subscription, setSubscription] = useState(null);
  const [schoolProfile, setSchoolProfile] = useState({ phone_no: '', email: '', address: '', business_logo: null, website: '', school_name: '', school_code: '' });
  const [savingSchool, setSavingSchool] = useState(false);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [autoRenewSaving, setAutoRenewSaving] = useState(false);
  const [autoRenewDialogOpen, setAutoRenewDialogOpen] = useState(false);

  const schoolId = useMemo(() => getUserData('admin')?.school_id, []);

  useEffect(() => {
    const fetchStats = async () => {
      if (!schoolId) {
        setError('School not found for this admin account.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const [schoolRes, totalUsersRes, activeStudentsRes, activeTeachersRes] = await Promise.all([
          adminSchoolApi.current(),
          adminUsersApi.list({ page_size: 1 }),
          adminUsersApi.list({ user_type: 'student', is_active: true, page_size: 1 }),
          adminUsersApi.list({ user_type: 'teacher', is_active: true, page_size: 1 }),
        ]);

        const extractCount = (payload) => payload?.count ?? payload?.results?.length ?? payload?.length ?? 0;

        setMetrics({
          totalUsers: extractCount(totalUsersRes.data) || schoolRes.data?.total_users_count || 0,
          activeStudents: extractCount(activeStudentsRes.data),
          activeTeachers: extractCount(activeTeachersRes.data),
        });
        setSubscription(schoolRes.data?.subscription_info || null);
        setSchoolProfile({
          phone_no: schoolRes.data?.phone_no || '',
          email: schoolRes.data?.email || '',
          address: schoolRes.data?.address || '',
          website: schoolRes.data?.website || '',
          business_logo: null,
          business_logo_url: schoolRes.data?.business_logo_url || '',
          school_name: schoolRes.data?.school_name || '',
          school_code: schoolRes.data?.school_code || '',
        });
      } catch (err) {
        console.error('Failed to load admin dashboard metrics', err);
        const status = err?.response?.status;
        if (status === 403) {
          setError('Access denied. Please check your permissions.');
        } else if (err?.response?.status === 401) {
          setError('Your session has expired. Please log in again.');
        } else {
          setError('Unable to load dashboard data right now.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [schoolId]);

  const handleSchoolSave = async () => {
    setSavingSchool(true);
    try {
      const formData = new FormData();
      formData.append('phone_no', schoolProfile.phone_no || '');
      formData.append('email', schoolProfile.email || '');
      formData.append('address', schoolProfile.address || '');
      formData.append('website', schoolProfile.website || '');
      if (schoolProfile.business_logo instanceof File) {
        formData.append('business_logo', schoolProfile.business_logo);
      }
      const res = await adminSchoolApi.updateProfile(formData);
      setSchoolProfile((prev) => ({ ...prev, business_logo_url: res.data?.business_logo_url || prev.business_logo_url, business_logo: null }));
      setIsEditingProfile(false);
      try { trackEvent('admin_school_profile_saved', attachUserProps({ page: 'admin_dashboard' })); } catch (e) {}
    } catch (err) {
      console.error('Failed to update school profile', err);
      setError(err.response?.data?.error || 'Unable to update school profile.');
    } finally {
      setSavingSchool(false);
    }
  };

  const handleConfirmToggleAutoRenew = async () => {
    if (!subscription) return;
    const next = !subscription.auto_renew;

    setAutoRenewSaving(true);
    try {
      const res = await adminSchoolApi.toggleAutoRenew({ auto_renew: next });
      const updatedSubscription = res.data?.subscription_info || { ...subscription, auto_renew: next };
      setSubscription(updatedSubscription);
      setAutoRenewDialogOpen(false);
      try { trackEvent('admin_auto_renew_toggled', attachUserProps({ auto_renew: updatedSubscription.auto_renew, page: 'admin_dashboard' })); } catch (e) {}
    } catch (err) {
      console.error('Failed to toggle auto renew', err);
      setError(err.response?.data?.error || 'Unable to update auto renew.');
    } finally {
      setAutoRenewSaving(false);
    }
  };

  const stats = [
    { id: 'total-users', title: 'Total Users', value: metrics.totalUsers, color: 'blue', icon: Users },
    { id: 'active-students', title: 'Active Students', value: metrics.activeStudents, color: 'green', icon: UserCheck },
    { id: 'teachers', title: 'Active Teachers', value: metrics.activeTeachers, color: 'purple', icon: School },
  ];

  return (
    <div className="flex flex-col h-full bg-[hsl(var(--main-bg))] p-6 overflow-y-auto">
      <div className="max-w-7xl mx-auto w-full space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Admin Dashboard</h1>
          <p className="text-muted-foreground mt-1">School overview and quick actions</p>
        </div>

        {error && (
          <Card className="bg-[hsl(var(--card-bg))] border-red-500/40">
            <CardContent className="p-4 text-red-400 text-sm">{error}</CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {stats.map((stat) => {
            const Icon = stat.icon;
            const colorClasses = {
              blue: 'bg-blue-500/10 text-blue-400 border-blue-500/50',
              green: 'bg-green-500/10 text-green-400 border-green-500/50',
              purple: 'bg-purple-500/10 text-purple-400 border-purple-500/50',
            };
            return (
              <Card key={stat.id} className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">{stat.title}</p>
                      <p className="text-3xl font-bold text-foreground">{loading ? <span className="text-muted-foreground">…</span> : stat.value}</p>
                      <p className="text-xs text-muted-foreground mt-1">Updated for your school</p>
                    </div>
                    <div className={`p-3 rounded-full border ${colorClasses[stat.color]}`}>
                      <Icon className="h-8 w-8" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <Card className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
          <CardHeader>
            <CardTitle className="text-foreground flex items-center gap-2"><Activity className="h-4 w-4" />What to do next</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="p-4 rounded-lg border border-[hsl(var(--card-border))] bg-[hsl(var(--main-bg))]">
              <p className="text-sm font-semibold text-foreground">Set up classes & divisions</p>
              <p className="text-xs text-muted-foreground mt-1">Organize grades and divisions for easier assignment.</p>
            </div>
            <div className="p-4 rounded-lg border border-[hsl(var(--card-border))] bg-[hsl(var(--main-bg))]">
              <p className="text-sm font-semibold text-foreground">Create teachers</p>
              <p className="text-xs text-muted-foreground mt-1">Share access with your teaching staff.</p>
            </div>
            <div className="p-4 rounded-lg border border-[hsl(var(--card-border))] bg-[hsl(var(--main-bg))]">
              <p className="text-sm font-semibold text-foreground">Add students</p>
              <p className="text-xs text-muted-foreground mt-1">Import or create new student accounts to get them started.</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
          <CardHeader>
            <CardTitle className="text-foreground">Subscription</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-foreground">
            {subscription ? (
              <>
                <div>
                  <p className="text-muted-foreground text-xs">Plan</p>
                  <p className="font-semibold">
                    {subscription.plan_tier || '—'}
                    {subscription && (
                      <span className="text-xs ml-1 text-muted-foreground">
                        ({subscription.country || '—'} • {metrics.activeStudents}/{subscription.max_students || '0'} students • {metrics.activeTeachers}/{subscription.max_teachers || '0'} teachers • {subscription.currency || '—'} {subscription.monthly_price || subscription.amount || '0.00'}/month • {subscription.currency || '—'} {subscription.yearly_price || '0.00'}/year)
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground">Status: {subscription.status || '—'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Billing cycle</p>
                  <p className="font-semibold capitalize">{subscription.billing_cycle || '—'}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <p className="text-xs text-muted-foreground">Auto renew: {subscription.auto_renew ? 'On' : 'Off'}</p>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => { setAutoRenewDialogOpen(true); try { trackEvent('admin_auto_renew_dialog_open', attachUserProps({ page: 'admin_dashboard' })); } catch (e) {} }}
                            disabled={autoRenewSaving}
                            className="border-[hsl(var(--card-border))]"
                          >
                            {autoRenewSaving ? 'Updating…' : subscription.auto_renew ? 'Turn Off' : 'Turn On'}
                          </Button>
                        </div>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Start date</p>
                  <p className="font-semibold">{subscription.start_date || '—'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">{subscription.auto_renew ? 'Renew date' : 'End date'}</p>
                  <p className="font-semibold">{subscription.end_date || '—'}</p>
                </div>
              </>
            ) : (
              <p className="text-muted-foreground">Subscription info unavailable. {error || 'Check permissions or configure school subscription.'}</p>
            )}
          </CardContent>
        </Card>

        <AlertDialog open={autoRenewDialogOpen} onOpenChange={setAutoRenewDialogOpen}>
          <AlertDialogContent className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white text-lg">Change auto renew</AlertDialogTitle>
              <AlertDialogDescription className="text-white text-sm space-y-1">
                <p>Plan: {subscription?.plan_tier || '—'} ({subscription?.billing_cycle || '—'})</p>
                <p>Current state: {subscription?.auto_renew ? 'On' : 'Off'}</p>
                <p>{subscription?.auto_renew ? 'Renew date' : 'End date'}: {subscription?.end_date || '—'}</p>
                <p>
                  {subscription?.auto_renew
                    ? 'If you turn it off, the subscription will not renew automatically after the current period.'
                    : 'If you turn it on, the subscription will renew automatically on the renewal date.'}
                </p>
                <p>Do you want to proceed?</p>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setAutoRenewDialogOpen(false)} disabled={autoRenewSaving} className="text-white hover:text-white">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction onClick={handleConfirmToggleAutoRenew} disabled={autoRenewSaving}>
                {autoRenewSaving ? 'Updating…' : 'Confirm'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <Card className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
          <CardHeader className="flex flex-row items-center justify-between gap-4">
            <CardTitle className="text-foreground">School profile</CardTitle>
            <div className="flex gap-2">
              {!isEditingProfile && (
                <Button variant="outline" size="sm" onClick={() => { setIsEditingProfile(true); try { trackEvent('admin_edit_profile_open', attachUserProps({ page: 'admin_dashboard' })); } catch (e) {} }} className="border-[hsl(var(--card-border))]">
                  Edit
                </Button>
              )}
              <Button onClick={handleSchoolSave} disabled={savingSchool || !isEditingProfile} size="sm">
                {savingSchool ? 'Saving…' : 'Save profile'}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-foreground">
            <div className="space-y-2">
              <Label className="text-muted-foreground text-xs">School Name</Label>
              <Input
                value={schoolProfile.school_name}
                className="bg-[hsl(var(--input-bg))] text-foreground"
                disabled
              />
              <Label className="text-muted-foreground text-xs">School Code</Label>
              <Input
                value={schoolProfile.school_code}
                className="bg-[hsl(var(--input-bg))] text-foreground"
                disabled
              />
              <Label className="text-muted-foreground text-xs">Email</Label>
              <Input
                value={schoolProfile.email}
                onChange={(e) => setSchoolProfile((p) => ({ ...p, email: e.target.value }))}
                placeholder="school@email.com"
                className="bg-[hsl(var(--input-bg))] text-foreground"
                disabled={!isEditingProfile}
              />
              <Label className="text-muted-foreground text-xs">Phone</Label>
              <Input
                value={schoolProfile.phone_no}
                onChange={(e) => setSchoolProfile((p) => ({ ...p, phone_no: e.target.value }))}
                placeholder="+1 555 1234"
                className="bg-[hsl(var(--input-bg))] text-foreground"
                disabled={!isEditingProfile}
              />
              <Label className="text-muted-foreground text-xs">Website</Label>
              <Input
                value={schoolProfile.website}
                onChange={(e) => setSchoolProfile((p) => ({ ...p, website: e.target.value }))}
                placeholder="https://school.edu"
                className="bg-[hsl(var(--input-bg))] text-foreground"
                disabled={!isEditingProfile}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-muted-foreground text-xs">Address</Label>
              <textarea
                value={schoolProfile.address}
                onChange={(e) => setSchoolProfile((p) => ({ ...p, address: e.target.value }))}
                className="w-full min-h-[120px] rounded-md border bg-[hsl(var(--input-bg))] text-foreground p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Street, City, Country"
                disabled={!isEditingProfile}
              />
              <Label className="text-muted-foreground text-xs">Logo</Label>
              <div className="flex items-center gap-3">
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setSchoolProfile((p) => ({ ...p, business_logo: e.target.files?.[0] || null }))}
                  className="bg-[hsl(var(--input-bg))] text-foreground"
                  disabled={!isEditingProfile}
                />
                {schoolProfile.business_logo_url && (
                  <img src={schoolProfile.business_logo_url} alt="School logo" className="h-12 w-12 rounded object-contain border border-[hsl(var(--card-border))]" />
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

